// filepath: cryptobag-com/src/components/MarketPulse.jsx
import React, { useMemo } from 'react';
import { formatCurrency } from '../utils/formatters';

/**
 * MarketPulse Component
 * Shows "gamified" cards for top gainer and loser.
 */
const MarketPulse = ({ coins, loading }) => {
  const [gainer, loser] = useMemo(() => {
    if (!coins || coins.length === 0) return [null, null];
    // Filter out coins with null data which can skew results
    const validCoins = coins.filter(c => c.price_change_percentage_24h_in_currency !== null);
    if (validCoins.length === 0) return [null, null];
    
    let topGainer = validCoins[0];
    let topLoser = validCoins[0];

    for (const coin of validCoins) {
      if (coin.price_change_percentage_24h_in_currency > topGainer.price_change_percentage_24h_in_currency) {
        topGainer = coin;
      }
      if (coin.price_change_percentage_24h_in_currency < topLoser.price_change_percentage_24h_in_currency) {
        topLoser = coin;
      }
    }
    return [topGainer, topLoser];
  }, [coins]);
  
  const PulseCardSkeleton = () => (
    <div className="h-24 bg-gray-800/60 backdrop-blur-sm border border-gray-700/50 rounded-lg p-4 animate-pulse">
      <div className="h-4 bg-gray-700 rounded w-1/3 mb-2"></div>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gray-700 rounded-full"></div>
          <div className="h-6 bg-gray-700 rounded w-20"></div>
        </div>
        <div className="h-8 bg-gray-700 rounded w-16"></div>
      </div>
    </div>
  );

  if (loading || !gainer || !loser) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PulseCardSkeleton />
        <PulseCardSkeleton />
      </div>
    );
  }

  const PulseCard = ({ coin, type }) => {
    const isGainer = type === 'gainer';
    const change = coin.price_change_percentage_24h_in_currency;
    
    return (
      <div className={`bg-gray-800/60 backdrop-blur-sm border border-gray-700/50 rounded-lg shadow-xl p-4 flex items-center justify-between transition-all duration-300 hover:shadow-2xl ${isGainer ? 'hover:border-green-500/50' : 'hover:border-red-500/50'} animate-fadeIn`}>
        <div>
          <div className="text-sm font-medium text-gray-400 mb-1">{isGainer ? 'Top Gainer (24h)' : 'Top Loser (24h)'}</div>
          <div className="flex items-center space-x-2">
            <img src={coin.image} alt={coin.name} className="w-8 h-8 rounded-full" />
            <span className="text-xl font-bold text-white">{coin.name}</span>
            <span className="text-xs text-gray-400 uppercase">{coin.symbol}</span>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-xl font-bold ${isGainer ? 'text-green-500' : 'text-red-500'}`}>
            {isGainer ? '+' : ''}{change?.toFixed(2)}%
          </div>
          <div className="text-sm text-gray-300">{formatCurrency(coin.current_price)}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <PulseCard coin={gainer} type="gainer" />
      <PulseCard coin={loser} type="loser" />
    </div>
  );
};

export default MarketPulse;